import React, { memo, useContext, useCallback, useEffect } from 'react';

import { SPACE_OVERVIEW, VIEW_PAGE } from '@confluence/named-routes/entry-points/routes.ts';
import { DelayRedirectionOnClient } from '@confluence/route-manager/entry-points/DelayRedirectionOnClient.ts';
import { Redirection } from '@confluence/route-manager/entry-points/Redirection.ts';
// eslint-disable-next-line no-restricted-imports -- deprecated - use RouteState instead
import { RoutesContext } from '@confluence/route-manager/entry-points/RoutesContext.ts';
import { useNavigationPolicy } from '@confluence/route-manager/entry-points/useNavigationPolicy.ts';
import { useIsCurrentPageLive } from '@confluence/live-pages-utils/entry-points/useIsCurrentPageLive.ts';

type ContentRedirectComponentProps = {
	spaceKeyInUrl: string | undefined;
	contentSlugInUrl: string | undefined;
	contentWithSlug: any;
	space: any;
	redirectToHideTitleSlug: boolean;
};

export const ContentRedirectComponent: React.MemoExoticComponent<
	({
		contentWithSlug,
		space,
		spaceKeyInUrl,
		contentSlugInUrl,
		redirectToHideTitleSlug,
	}: ContentRedirectComponentProps) => React.JSX.Element | null
> = memo(
	({
		contentWithSlug,
		space,
		spaceKeyInUrl,
		contentSlugInUrl = '',
		redirectToHideTitleSlug,
	}: ContentRedirectComponentProps): React.JSX.Element | null => {
		const { match, replace, toUrl, getHash, getQueryParams } = useContext(RoutesContext);
		const { routePolicy } = useNavigationPolicy();
		const isLivePage = useIsCurrentPageLive();

		const shouldRedirectIncorrectSpaceKey = useCallback(() => {
			if (isLivePage) {
				/*
				 * Live Pages exist on the view page route, but render the Confluence editor
				 * The Confluence editor has its own space key redirection logic and move event listeners (via useSpaceKeyWatcher)
				 * Consequently this redirection will clash with the redirects from the editor.
				 */
				return false;
			}
			return contentWithSlug && spaceKeyInUrl !== contentWithSlug?.spaceKey;
		}, [spaceKeyInUrl, contentWithSlug, isLivePage]);

		const shouldRedirectIncorrectSlug = useCallback(() => {
			// When current slug in URL does not match with slug in content ==> slug changed
			// Important to decode when compare because react router decodes the params first
			// so contentSlug will be decoded string
			//
			// Live pages will handle its own redirection logic from content slug
			return isLivePage
				? false
				: decodeURIComponent(contentWithSlug?.slug || '') !== decodeURIComponent(contentSlugInUrl);
		}, [contentWithSlug?.slug, contentSlugInUrl, isLivePage]);

		const isIncorrectSlug = shouldRedirectIncorrectSlug();
		const isIncorrectSpaceKey = shouldRedirectIncorrectSpaceKey();
		const hasContentTitleToStrip = redirectToHideTitleSlug && isIncorrectSlug;
		const hasAnchorHashToStrip = redirectToHideTitleSlug && Boolean(getHash());

		// Strip the content title slug and/or heading anchor hash from the URL when the
		// hide-title-in-url setting and experiment are enabled.
		// This runs as a useEffect (rather than relying on <Redirection>) so that it fires on every navigation,
		// including revisits to pages / blogs.
		useEffect(() => {
			if (!contentWithSlug || isIncorrectSpaceKey) {
				return;
			}

			if (!hasContentTitleToStrip && !hasAnchorHashToStrip) {
				return;
			}

			window['__DELAYED_CONTENT_REDIRECTION__'] = {};

			const targetUrl = toUrl(match?.name, {
				...match?.params,
				// When hiding title from the address bar URL, also strip heading anchor hashes to avoid exposing UGC in the address bar.
				hash: undefined,
				query: getQueryParams(),
				spaceKey: contentWithSlug.spaceKey,
				contentId: contentWithSlug.id,
				contentSlug: contentWithSlug.slug,
			});

			replace(targetUrl);
		}, [
			hasContentTitleToStrip,
			hasAnchorHashToStrip,
			isIncorrectSpaceKey,
			contentWithSlug,
			match?.name,
			match?.params,
			replace,
			toUrl,
			getHash,
			getQueryParams,
		]);

		// Don't redirect if we have insufficient information to successfully redirect the user
		if (!contentWithSlug) {
			return null;
		}

		// Do redirect if we should go back to the Space Home Overview Tab
		if (space && contentWithSlug.id === `${space.homepageId}` && match?.name === VIEW_PAGE.name) {
			const willPushInSelf =
				!routePolicy ||
				routePolicy.willPushInSelf(
					VIEW_PAGE.name,
					match?.params.contentId,
					SPACE_OVERVIEW.name,
					contentWithSlug.id,
				);

			return willPushInSelf ? (
				<Redirection
					name={SPACE_OVERVIEW.name}
					params={{
						spaceKey: contentWithSlug.spaceKey,
						contentId: contentWithSlug.id,
						contentSlug: contentWithSlug.slug,
					}}
				/>
			) : null;
		}

		// Incorrect slug cannot do redirection in SSR, if there is #anchor fragment in the URL,
		// it will be lost in the redirection, as #anchor fragment is not passed to the server.
		// So we need to do redirection in client side.
		// If the content slug is already fixed in PageState, but URL is incorrect, rewrite URL without
		// triggering route change.
		if (
			!isIncorrectSpaceKey &&
			(isIncorrectSlug || window.__DELAYED_CONTENT_REDIRECTION__?.contentSlug)
		) {
			return (
				<DelayRedirectionOnClient
					name={match?.name}
					reason={`CRC-wrongSK:${isIncorrectSpaceKey}|wrongSlug:${isIncorrectSlug}`}
					params={{
						...match?.params,
						spaceKey: contentWithSlug.spaceKey,
						contentId: contentWithSlug.id,
						contentSlug: contentWithSlug.slug,
					}}
					skipRouteChange={Boolean(!isIncorrectSlug)} // Only skip route change if slug is correct
				/>
			);
		}

		// Don't redirect if we otherwise don't need to
		if (!isIncorrectSpaceKey && !isIncorrectSlug) {
			return null;
		}

		return (
			<Redirection
				name={match?.name}
				reason={`CRC-wrongSK:${isIncorrectSpaceKey}|wrongSlug:${isIncorrectSlug}`}
				params={{
					...match?.params,
					spaceKey: contentWithSlug.spaceKey,
					contentId: contentWithSlug.id,
					contentSlug: contentWithSlug.slug,
				}}
			/>
		);
	},
);
